// =============================================================================
// TEAM SECTION COMPONENT (V2 - NORMALIZED)
// =============================================================================

import React from 'react';
import { Linkedin, Twitter, Github, Mail } from 'lucide-react';
import { layoutToClasses, displayToStyles } from '@/types/layout';
import type { ContentComponent } from '@/types';
import type { TeamComponent } from '@/types/components';

const socialIconMap = {
  linkedin: Linkedin,
  twitter: Twitter,
  github: Github,
  email: Mail,
};

interface TeamSectionProps {
  component: ContentComponent;
}

/**
 * Type guard for new TeamComponent format
 */
function isNewTeamFormat(component: ContentComponent): boolean {
  return (
    component.type === 'team' &&
    'items' in component &&
    Array.isArray((component as any).items)
  );
}

/**
 * Team Section Component
 */
export const TeamSection: React.FC<TeamSectionProps> = ({ component }) => {
  if (isNewTeamFormat(component)) {
    return <TeamSectionNew component={component as unknown as any} />;
  }
  return <TeamSectionLegacy component={component} />;
};

/**
 * Render social links
 */
const renderSocialLinks = (social?: Record<string, string>) => {
  if (!social) return null;
  
  return Object.entries(social).map(([platform, url]) => {
    if (!url) return null;
    
    const IconComponent = socialIconMap[platform as keyof typeof socialIconMap];
    if (!IconComponent) return null;
    
    return (
      <a
        key={platform}
        href={url}
        target="_blank"
        rel="noopener noreferrer"
        className={`social-link ${platform}`}
        aria-label={`${platform} profile`}
      >
        <IconComponent size={20} />
      </a>
    );
  });
};

/**
 * New format Team Section
 */
const TeamSectionNew: React.FC<{ component: TeamComponent }> = ({ component }) => {
  const { id, content, items, layout, display } = component;
  
  const layoutClasses = layoutToClasses(layout);
  const displayStyles = displayToStyles(display);
  
  return (
    <section id={id} className={`content-section team-section ${layoutClasses}`} style={displayStyles}>
      <div className="container">
        {content.title && (
          <div className="section-header">
            <h2>{content.title}</h2>
            {content.description && <p>{content.description}</p>}
          </div>
        )}
        
        <div className={`team-grid grid-cols-${layout.columns || 3}`}>
          {items.map((member, index) => (
            <div key={member.id || index} className="team-member">
              <div className="member-card">
                <div className="member-avatar">
                  {member.avatar ? (
                    <img src={member.avatar} alt={member.name} loading="lazy" />
                  ) : (
                    <div className="avatar-placeholder">
                      <span>{member.name.split(' ').map(n => n[0]).join('')}</span>
                    </div>
                  )}
                </div>
                
                <div className="member-info">
                  <h3 className="member-name">{member.name}</h3>
                  <p className="member-role">{member.role}</p>
                  
                  {member.bio && (
                    <p className="member-bio">{member.bio}</p>
                  )}
                  
                  {member.social && Object.keys(member.social).length > 0 && (
                    <div className="member-social">
                      {renderSocialLinks(member.social)}
                    </div>
                  )}
                </div>
              </div>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
};

/**
 * Legacy format Team Section
 */
const TeamSectionLegacy: React.FC<{ component: ContentComponent }> = ({ component }) => {
  const { id, title, content, metadata } = component;
  
  const team = metadata?.team || [];
  const columns = metadata?.columns || 3;
  
  return (
    <section id={id} className="content-section team-section">
      <div className="container">
        {title && (
          <div className="section-header">
            <h2>{title}</h2>
            {content && <p>{content}</p>}
          </div>
        )}
        
        <div className={`team-grid grid-cols-${columns}`}>
          {team.map((member: any, index: number) => (
            <div key={index} className="team-member">
              <div className="member-card">
                <div className="member-avatar">
                  {member.avatar ? (
                    <img src={member.avatar} alt={member.name} loading="lazy" />
                  ) : (
                    <div className="avatar-placeholder">
                      <span>{member.name.split(' ').map((n: string) => n[0]).join('')}</span>
                    </div>
                  )}
                </div>
                
                <div className="member-info">
                  <h3 className="member-name">{member.name}</h3>
                  <p className="member-role">{member.role}</p>
                  
                  {member.bio && (
                    <p className="member-bio">{member.bio}</p>
                  )}
                  
                  {member.social && Object.keys(member.social).length > 0 && (
                    <div className="member-social">
                      {renderSocialLinks(member.social)}
                    </div>
                  )}
                </div>
              </div>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
};

export { TeamSectionNew, TeamSectionLegacy };

